import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  UserProfileDto,
  UserRole,
  CreateAssessmentDto,
  AssessmentDto,
} from "@green0meter/api-client";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";

import { DOMAIN_DOUBLE_MATERIALITY } from "../domain";

import { roleGuard } from "@api";
import { doubleMaterialityAssessmentsApi } from "@api/services";

export const ACTION_CREATE_ASSESSMENT = "CREATE_ASSESSMENT";
const ACTION_GET_ASSESSMENTS = "GET_ASSESSMENTS";

const allowedRoles = [UserRole.Owner, UserRole.Manager];

interface CreateAssessmentResponse {
  data: AssessmentDto;
}

const createAssessment = async (
  data: CreateAssessmentDto,
): Promise<CreateAssessmentResponse> => {
  const response =
    await doubleMaterialityAssessmentsApi.doubleMaterialityAssessmentsControllerCreateAssessment(
      data,
    );

  return {
    data: response.data,
  };
};

export const useCreateAssessment = (
  user: UserProfileDto | null,
): {
  createAssessment: (data: CreateAssessmentDto) => void;
  createAssessmentAsync: (
    data: CreateAssessmentDto,
  ) => Promise<CreateAssessmentResponse>;
  isPendingCreateAssessment: boolean;
  isSuccessCreateAssessment: boolean;
  isErrorCreateAssessment: boolean;
  errorCreateAssessment: Error | null;
  enabled: boolean;
} => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const enabled = roleGuard(user, allowedRoles);

  const mutation = useMutation({
    mutationFn: createAssessment,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [DOMAIN_DOUBLE_MATERIALITY, ACTION_GET_ASSESSMENTS],
      });
      enqueueSnackbar(t("assessments.messages.createSuccess"), {
        variant: "success",
      });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || t("assessments.messages.createError"), {
        variant: "error",
      });
    },
  });

  return {
    createAssessment: mutation.mutate,
    createAssessmentAsync: mutation.mutateAsync,
    isPendingCreateAssessment: mutation.isPending,
    isSuccessCreateAssessment: mutation.isSuccess,
    isErrorCreateAssessment: mutation.isError,
    errorCreateAssessment: mutation.error,
    enabled,
  };
};
